import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';
import { getArticleImage } from '@/mocks/article-images';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">文章不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const imageUrl = getArticleImage(article.slug);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: imageUrl,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '做爱指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://relationx.github.io/article/${article.slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <article className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              {/* Breadcrumb */}
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    ...(category
                      ? [{ label: category.name, path: `/category/${category.slug}` }]
                      : []),
                    { label: article.title },
                  ]}
                />
              </div>

              {/* Article Header */}
              <header className="mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4 leading-snug">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line text-xs"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line text-xs"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line text-xs"></i> {article.readTime}
                  </span>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="px-2.5 py-0.5 bg-accent-50 text-accent-600 rounded-full text-xs font-medium hover:bg-accent-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>
              </header>

              {/* Featured Image */}
              <div className="mb-8 rounded-lg overflow-hidden bg-background-200">
                <img
                  src={imageUrl}
                  alt={article.title}
                  title={`${article.title} - 做爱指南`}
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              ></div>

              {/* Tags */}
              <div className="mt-10 pt-6 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 mt-8 bg-background-100">
              <div className="max-w-6xl mx-auto">
                <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.slug} article={a} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}